// Formatting helpers for the calendar renderer (KSE-0191). Pure functions over
// the CalendarShellSnapshot the shared Swift core publishes: every layout
// coordinate comes from the snapshot, so nothing here does date math beyond
// what Intl needs to print a label. Civil dates (CalendarDate) are printed
// through a UTC-pinned Date so the display zone can never shift them; instants
// are printed in the snapshot's display zone.
import type {
  CalendarDate,
  CalendarDateRange,
  CalendarEvent,
  CalendarOccurrence,
  CalendarShellSnapshot,
  CalendarViewMode,
  JMAPCalendar,
} from "../core/shell-types";

// ---------------------------------------------------------------------------
// Civil dates

export function sameDate(a: CalendarDate, b: CalendarDate): boolean {
  return a.year === b.year && a.month === b.month && a.day === b.day;
}

// "2026-08-19" — the data-date hook and React keys.
export function dateKey(date: CalendarDate): string {
  return `${date.year}-${pad2(date.month)}-${pad2(date.day)}`;
}

function pad2(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

// A Date whose UTC components are the civil date, for formatting with
// `timeZone: "UTC"`. Never read this back through a local zone.
export function civilDate(date: CalendarDate): Date {
  return new Date(Date.UTC(date.year, date.month - 1, date.day, 12));
}

const CIVIL = { timeZone: "UTC" } as const;

// The nominal days of an all-day occurrence (inclusive), mirroring
// CalendarOccurrence.allDayRange: `start`/`end` are UTC midnights, end
// exclusive. Null for timed occurrences.
export function allDayRange(occurrence: CalendarOccurrence): CalendarDateRange | null {
  if (!occurrence.isAllDay) {
    return null;
  }
  const start = new Date(occurrence.start);
  const endExclusive = new Date(occurrence.end);
  const first: CalendarDate = {
    year: start.getUTCFullYear(),
    month: start.getUTCMonth() + 1,
    day: start.getUTCDate(),
  };
  // Step back one millisecond from the exclusive end and read the day: the
  // last nominal day when the end is a midnight, the same day otherwise.
  const lastInstant = new Date(Math.max(endExclusive.getTime() - 1, start.getTime()));
  const last: CalendarDate = {
    year: lastInstant.getUTCFullYear(),
    month: lastInstant.getUTCMonth() + 1,
    day: lastInstant.getUTCDate(),
  };
  return { start: first, end: last };
}

// ---------------------------------------------------------------------------
// Locale + zone plumbing

// Intl throws RangeError on a zone it does not know (the core may report a
// fixed-offset name older engines cannot parse); fall back to the browser's.
export function safeTimeZone(timeZone: string | null | undefined): string | undefined {
  if (!timeZone) {
    return undefined;
  }
  try {
    new Intl.DateTimeFormat("en-US", { timeZone }).format(0);
    return timeZone;
  } catch {
    return undefined;
  }
}

export function resolvedTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

interface WeekInfoLocale extends Intl.Locale {
  getWeekInfo?: () => { firstDay: number };
  weekInfo?: { firstDay: number };
}

// The locale's week start as the core counts it (1 = Sunday … 7 = Saturday).
// Intl.Locale weekInfo reports ISO days (1 = Monday … 7 = Sunday).
export function firstWeekdayFromLocale(locale?: string): number {
  try {
    const tag = locale ?? (typeof navigator !== "undefined" ? navigator.language : "en-US");
    const info = new Intl.Locale(tag) as WeekInfoLocale;
    const week = info.getWeekInfo?.() ?? info.weekInfo;
    if (week && typeof week.firstDay === "number") {
      return isoWeekdayToCalendar(week.firstDay);
    }
  } catch {
    // Fall through to Sunday.
  }
  return 1;
}

export function isoWeekdayToCalendar(isoDay: number): number {
  return isoDay === 7 ? 1 : isoDay + 1;
}

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatter(locale: string | undefined, options: Intl.DateTimeFormatOptions) {
  const key = `${locale ?? ""}|${JSON.stringify(options)}`;
  let cached = formatterCache.get(key);
  if (!cached) {
    cached = new Intl.DateTimeFormat(locale, options);
    formatterCache.set(key, cached);
  }
  return cached;
}

function civil(date: CalendarDate, options: Intl.DateTimeFormatOptions, locale?: string) {
  return formatter(locale, { ...options, ...CIVIL }).format(civilDate(date));
}

// ---------------------------------------------------------------------------
// Labels for civil dates

// "19"
export function dayNumberLabel(date: CalendarDate, locale?: string): string {
  return civil(date, { day: "numeric" }, locale);
}

// "Wed" for weekday 4 (1 = Sunday … 7 = Saturday).
export function weekdayShort(weekday: number, locale?: string): string {
  return weekdayLabel(weekday, "short", locale);
}

// "Wednesday"
export function weekdayLong(weekday: number, locale?: string): string {
  return weekdayLabel(weekday, "long", locale);
}

// "W"
export function weekdayNarrow(weekday: number, locale?: string): string {
  return weekdayLabel(weekday, "narrow", locale);
}

function weekdayLabel(weekday: number, width: "short" | "long" | "narrow", locale?: string) {
  // 2023-01-01 was a Sunday; weekday 1 = Sunday.
  const date = new Date(Date.UTC(2023, 0, ((weekday - 1) % 7) + 1, 12));
  return formatter(locale, { weekday: width, ...CIVIL }).format(date);
}

// "August 2026"
export function monthYearLabel(date: CalendarDate, locale?: string): string {
  return civil(date, { month: "long", year: "numeric" }, locale);
}

// "Aug 19"
export function monthDayLabel(date: CalendarDate, locale?: string): string {
  return civil(date, { month: "short", day: "numeric" }, locale);
}

// "Aug 19, 2026"
export function monthDayYearLabel(date: CalendarDate, locale?: string): string {
  return civil(date, { month: "short", day: "numeric", year: "numeric" }, locale);
}

// "Wednesday, August 19"
export function weekdayMonthDayLabel(date: CalendarDate, locale?: string): string {
  return civil(date, { weekday: "long", month: "long", day: "numeric" }, locale);
}

// "Wednesday, August 19, 2026"
export function fullDateLabel(date: CalendarDate, locale?: string): string {
  return civil(date, { weekday: "long", month: "long", day: "numeric", year: "numeric" }, locale);
}

// Agenda section header: "Wed, Aug 19" (plus the year when it is not today's).
export function agendaSectionLabel(date: CalendarDate, today: CalendarDate, locale?: string) {
  const options: Intl.DateTimeFormatOptions = { weekday: "short", month: "short", day: "numeric" };
  if (date.year !== today.year) {
    options.year = "numeric";
  }
  return civil(date, options, locale);
}

// "Aug 17 – 23, 2026", "Aug 31 – Sep 6, 2026", "Dec 29, 2025 – Jan 4, 2026".
export function dateRangeLabel(
  range: CalendarDateRange,
  locale?: string,
  { year = true }: { year?: boolean } = {},
): string {
  const { start, end } = range;
  if (sameDate(start, end)) {
    return year ? monthDayYearLabel(start, locale) : monthDayLabel(start, locale);
  }
  if (start.year !== end.year) {
    return `${monthDayYearLabel(start, locale)} – ${monthDayYearLabel(end, locale)}`;
  }
  const suffix = year ? `, ${civil(end, { year: "numeric" }, locale)}` : "";
  if (start.month !== end.month) {
    return `${monthDayLabel(start, locale)} – ${monthDayLabel(end, locale)}${suffix}`;
  }
  return `${monthDayLabel(start, locale)} – ${dayNumberLabel(end, locale)}${suffix}`;
}

// The toolbar title for a view mode.
// `compact` picks the shorter forms a phone-width toolbar can fit
// ("Wed, Aug 19", "Aug 16 – 22").
export function rangeTitle(
  snapshot: Pick<CalendarShellSnapshot, "viewMode" | "anchor" | "range">,
  locale?: string,
  { compact = false }: { compact?: boolean } = {},
): string {
  switch (snapshot.viewMode) {
    case "month":
      return monthYearLabel(snapshot.anchor, locale);
    case "week":
      return dateRangeLabel(snapshot.range, locale, { year: !compact });
    case "day":
      return compact
        ? civil(snapshot.anchor, { weekday: "short", month: "short", day: "numeric" }, locale)
        : weekdayMonthDayLabel(snapshot.anchor, locale);
    case "agenda":
      return dateRangeLabel(snapshot.range, locale, { year: false });
    default:
      return dateRangeLabel(snapshot.range, locale);
  }
}

// ---------------------------------------------------------------------------
// Labels for instants (timed occurrences), in the display zone

// "1:00 PM"
export function timeLabel(instant: string | Date, timeZone: string, locale?: string): string {
  const date = typeof instant === "string" ? new Date(instant) : instant;
  return formatter(locale, {
    hour: "numeric",
    minute: "2-digit",
    timeZone: safeTimeZone(timeZone),
  }).format(date);
}

// Compact chip time: "1 PM" on the hour, else "1:30 PM".
export function shortTimeLabel(instant: string | Date, timeZone: string, locale?: string): string {
  const date = typeof instant === "string" ? new Date(instant) : instant;
  const zone = safeTimeZone(timeZone);
  const minute = Number(
    formatter("en-US", { minute: "numeric", timeZone: zone }).format(date).replace(/\D/g, ""),
  );
  if (minute === 0) {
    return formatter(locale, { hour: "numeric", timeZone: zone }).format(date);
  }
  return timeLabel(date, timeZone, locale);
}

// "1:00 PM – 2:30 PM"
export function timeRangeLabel(
  start: string | Date,
  end: string | Date,
  timeZone: string,
  locale?: string,
): string {
  return `${timeLabel(start, timeZone, locale)} – ${timeLabel(end, timeZone, locale)}`;
}

// The gutter label of an hour row: "12 AM", "1 AM", … "11 PM".
export function hourLabel(hour: number, locale?: string): string {
  const date = new Date(Date.UTC(2023, 0, 1, hour));
  return formatter(locale, { hour: "numeric", ...CIVIL }).format(date);
}

// "Wed, Aug 19, 2026" of an instant, in the display zone.
export function instantDateLabel(instant: string | Date, timeZone: string, locale?: string) {
  const date = typeof instant === "string" ? new Date(instant) : instant;
  return formatter(locale, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: safeTimeZone(timeZone),
  }).format(date);
}

// True when both instants fall on the same civil day of the display zone.
export function sameDisplayDay(a: string | Date, b: string | Date, timeZone: string): boolean {
  const zone = safeTimeZone(timeZone);
  const f = formatter("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: zone,
  });
  return (
    f.format(typeof a === "string" ? new Date(a) : a) ===
    f.format(typeof b === "string" ? new Date(b) : b)
  );
}

// "Aug 19, 2026" or "Aug 22 – 23, 2026" for an all-day occurrence.
export function allDayLabel(range: CalendarDateRange, locale?: string): string {
  return dateRangeLabel(range, locale);
}

// The "when" line of the detail view / agenda row.
export function occurrenceWhenLabel(
  occurrence: CalendarOccurrence,
  timeZone: string,
  locale?: string,
): string {
  const days = allDayRange(occurrence);
  if (days) {
    return `${allDayLabel(days, locale)} · All day`;
  }
  const startDay = instantDateLabel(occurrence.start, timeZone, locale);
  if (sameDisplayDay(occurrence.start, occurrence.end, timeZone)) {
    return `${startDay} · ${timeRangeLabel(occurrence.start, occurrence.end, timeZone, locale)}`;
  }
  const endDay = instantDateLabel(occurrence.end, timeZone, locale);
  return `${startDay} ${timeLabel(occurrence.start, timeZone, locale)} – ${endDay} ${timeLabel(
    occurrence.end,
    timeZone,
    locale,
  )}`;
}

// The time cell of an agenda row: "All day", "1:00 PM – 2:30 PM", or the
// spill-over of a multi-day timed occurrence ("→ 6:15 AM", "10:30 PM →").
export function agendaTimeLabel(
  occurrence: CalendarOccurrence,
  day: CalendarDate,
  timeZone: string,
  locale?: string,
): string {
  if (occurrence.isAllDay) {
    return "All day";
  }
  const dayStart = displayDayOf(occurrence.start, timeZone);
  const dayEnd = displayDayOf(new Date(new Date(occurrence.end).getTime() - 1), timeZone);
  const startsToday = dayStart === dateKey(day);
  const endsToday = dayEnd === dateKey(day);
  if (startsToday && endsToday) {
    return timeRangeLabel(occurrence.start, occurrence.end, timeZone, locale);
  }
  if (startsToday) {
    return `${timeLabel(occurrence.start, timeZone, locale)} →`;
  }
  if (endsToday) {
    return `→ ${timeLabel(occurrence.end, timeZone, locale)}`;
  }
  return "All day";
}

// "YYYY-MM-DD" of an instant in the display zone.
export function displayDayOf(instant: string | Date, timeZone: string): string {
  const parts = formatter("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: safeTimeZone(timeZone),
  }).formatToParts(typeof instant === "string" ? new Date(instant) : instant);
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

// The zone name a detail view shows next to a timed event whose own zone
// differs from the display zone ("Europe/Berlin").
export function differingZone(
  occurrence: Pick<CalendarOccurrence, "timeZone" | "isAllDay">,
  displayZone: string,
): string | null {
  if (occurrence.isAllDay || !occurrence.timeZone) {
    return null;
  }
  return occurrence.timeZone === displayZone ? null : occurrence.timeZone;
}

// "Synced 3 minutes ago"-style label; plain time when older than a day.
export function lastSyncedLabel(iso: string, now: Date = new Date(), locale?: string): string {
  const date = new Date(iso);
  const seconds = Math.round((now.getTime() - date.getTime()) / 1000);
  if (Number.isNaN(seconds)) {
    return "";
  }
  if (seconds < 60) {
    return "Synced just now";
  }
  if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    return `Synced ${minutes} min ago`;
  }
  if (seconds < 86400) {
    return `Synced at ${formatter(locale, { hour: "numeric", minute: "2-digit" }).format(date)}`;
  }
  return `Synced ${formatter(locale, { month: "short", day: "numeric" }).format(date)}`;
}

// ---------------------------------------------------------------------------
// Colours

// Used when neither the event nor its calendar carries a colour; indexed by
// the calendar's position so each calendar keeps one hue.
export const FALLBACK_PALETTE = [
  "#3B82F6",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#8B5CF6",
  "#EC4899",
  "#14B8A6",
  "#F97316",
] as const;

export function normalizeHexColor(color: string | null | undefined): string | null {
  if (!color) {
    return null;
  }
  const trimmed = color.trim();
  const short = /^#?([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(trimmed);
  if (short) {
    return `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`.toUpperCase();
  }
  const long = /^#?([0-9a-f]{6})([0-9a-f]{2})?$/i.exec(trimmed);
  if (long) {
    return `#${long[1]}`.toUpperCase();
  }
  return null;
}

// Mirrors CalendarShellSnapshot.calendar(withId:).
export function calendarById(
  snapshot: Pick<CalendarShellSnapshot, "calendars">,
  calendarId: string,
): JMAPCalendar | undefined {
  return snapshot.calendars.find((calendar) => calendar.id === calendarId);
}

// The colour of a calendar swatch: its own, else the palette by position.
export function calendarColor(
  snapshot: Pick<CalendarShellSnapshot, "calendars">,
  calendarId: string,
): string {
  const index = snapshot.calendars.findIndex((calendar) => calendar.id === calendarId);
  const own = index >= 0 ? normalizeHexColor(snapshot.calendars[index].color) : null;
  if (own) {
    return own;
  }
  const slot = index >= 0 ? index : hashString(calendarId);
  return FALLBACK_PALETTE[slot % FALLBACK_PALETTE.length];
}

// Mirrors CalendarShellSnapshot.color(for:): the event's own colour, else its
// calendar's, else the fallback palette.
export function occurrenceColor(
  snapshot: Pick<CalendarShellSnapshot, "calendars">,
  occurrence: Pick<CalendarOccurrence, "color" | "calendarId">,
): string {
  return normalizeHexColor(occurrence.color) ?? calendarColor(snapshot, occurrence.calendarId);
}

function hashString(value: string): number {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }
  return hash;
}

// WCAG relative luminance of "#RRGGBB" (0 = black … 1 = white).
export function relativeLuminance(hex: string): number {
  const normalized = normalizeHexColor(hex) ?? "#000000";
  const channel = (offset: number) => {
    const value = Number.parseInt(normalized.slice(offset, offset + 2), 16) / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
}

// Text colour that reads on a chip of the given background.
export function textColorOn(background: string): "#FFFFFF" | "#0F172A" {
  // Contrast against white vs near-black; pick the larger ratio.
  const luminance = relativeLuminance(background);
  const withWhite = 1.05 / (luminance + 0.05);
  const withDark = (luminance + 0.05) / (0.0117 + 0.05);
  return withWhite >= withDark ? "#FFFFFF" : "#0F172A";
}

// ---------------------------------------------------------------------------
// Occurrence lookups (mirror CalendarShellSnapshot helpers)

export function occurrenceForKey(
  snapshot: Pick<CalendarShellSnapshot, "occurrences">,
  key: string,
): CalendarOccurrence | undefined {
  return snapshot.occurrences.find((occurrence) => occurrence.key === key);
}

export function occurrenceIndex(
  snapshot: Pick<CalendarShellSnapshot, "occurrences">,
): Map<string, CalendarOccurrence> {
  const map = new Map<string, CalendarOccurrence>();
  for (const occurrence of snapshot.occurrences) {
    map.set(occurrence.key, occurrence);
  }
  return map;
}

// ---------------------------------------------------------------------------
// Event detail helpers

// Plain text of an event description: HTML descriptions are stripped of tags
// (never rendered as markup); block boundaries become line breaks.
export function descriptionText(
  event: Pick<CalendarEvent, "description" | "descriptionContentType">,
): string {
  const raw = event.description ?? "";
  if (!/html/i.test(event.descriptionContentType ?? "")) {
    return raw;
  }
  return htmlToText(raw);
}

// A numeric character reference as text. `String.fromCodePoint` throws for
// anything outside Unicode (e.g. `&#9999999999;`, `&#x110000;`) and this runs
// inside a render, so out-of-range or surrogate code points decode to U+FFFD
// instead of taking the surface down with a RangeError.
function decodeCodePoint(code: number): string {
  if (
    !Number.isInteger(code) ||
    code < 0 ||
    code > 0x10ffff ||
    (code >= 0xd800 && code <= 0xdfff)
  ) {
    return "�";
  }
  return String.fromCodePoint(code);
}

export function htmlToText(html: string): string {
  const text = html
    .replace(/<\s*(script|style)[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, "")
    .replace(/<\s*br\s*\/?\s*>/gi, "\n")
    .replace(/<\s*\/\s*(p|div|li|h[1-6]|tr|blockquote|pre)\s*>/gi, "\n")
    .replace(/<\s*(li)\b[^>]*>/gi, "• ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&#(\d+);/g, (_match, code: string) => decodeCodePoint(Number(code)))
    .replace(/&#x([0-9a-f]+);/gi, (_match, code: string) =>
      decodeCodePoint(Number.parseInt(code, 16)),
    );
  return text
    .split("\n")
    .map((line) => line.replace(/[ \t]+/g, " ").trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function participationStatusLabel(status: string): string {
  switch (status) {
    case "accepted":
      return "Accepted";
    case "declined":
      return "Declined";
    case "tentative":
      return "Tentative";
    case "delegated":
      return "Delegated";
    default:
      return "Awaiting reply";
  }
}

export function eventStatusLabel(status: string): string {
  switch (status) {
    case "tentative":
      return "Tentative";
    case "cancelled":
      return "Cancelled";
    default:
      return "Confirmed";
  }
}

export function freeBusyLabel(status: string): string {
  return status === "free" ? "Free" : "Busy";
}

// Only http(s)/mailto/tel links are rendered as anchors.
export function safeHref(uri: string | null | undefined): string | null {
  if (!uri) {
    return null;
  }
  const trimmed = uri.trim();
  if (/^(https?:|mailto:|tel:)/i.test(trimmed)) {
    return trimmed;
  }
  return null;
}

// The view modes offered by the switcher, in display order.
export const VIEW_MODES: readonly CalendarViewMode[] = ["day", "week", "month", "agenda"];

export function viewModeLabel(mode: CalendarViewMode): string {
  switch (mode) {
    case "day":
      return "Day";
    case "week":
      return "Week";
    case "month":
      return "Month";
    default:
      return "Agenda";
  }
}
